import { ReactTable } from "@aix/ui"
import React, { useState } from "react"
import toast from "react-hot-toast"

import { trpc } from "../../lib/trpc"

type TabType = "send" | "preferences" | "analytics" | "logs"

interface NotificationFormData {
  channel: "slack" | "telegram" | "email" | "desktop"
  title: string
  message: string
  priority: "low" | "medium" | "high" | "urgent"
  category: "system" | "user" | "business" | "personal" | "marketing"
  interactive: boolean
  actions: {
    id: string
    text: string
    value: string
    style?: "primary" | "danger" | "default"
    url?: string
  }[]
  slackChannelId?: string
  telegramChatId?: string
  emailTo?: string
}

export function NotificationScreen() {
  const [activeTab, setActiveTab] = useState<TabType>("send")
  const [formData, setFormData] = useState<NotificationFormData>({
    channel: "slack",
    title: "",
    message: "",
    priority: "medium",
    category: "system",
    interactive: false,
    actions: [],
  })

  const sendMutation = trpc.notifications.send.useMutation()
  const quickNotifyMutation = trpc.notifications.quickNotify.useMutation()
  const testMutation = trpc.notifications.test.useMutation()
  const logsQuery = trpc.notifications.getLogs.useQuery({ limit: 50 })
  const statsQuery = trpc.notifications.getStats.useQuery({ period: "day" })
  const userPrefsQuery = trpc.notifications.getUserPreferences.useQuery({})
  const setPreferencesMutation = trpc.notifications.setUserPreferences.useMutation()

  const handleSendNotification = async () => {
    try {
      const options: any = {
        channel: formData.channel,
        title: formData.title,
        message: formData.message,
        priority: formData.priority,
        category: formData.category,
        interactive: formData.interactive,
      }

      if (formData.interactive && formData.actions.length > 0) {
        options.actions = formData.actions
      }

      // Channel-specific options
      if (formData.channel === "slack" && formData.slackChannelId) {
        options.slackOptions = { channelId: formData.slackChannelId }
      }
      if (formData.channel === "telegram" && formData.telegramChatId) {
        options.telegramOptions = { chatId: formData.telegramChatId }
      }
      if (formData.channel === "email" && formData.emailTo) {
        options.userId = formData.emailTo
      }

      const result = await sendMutation.mutateAsync(options)

      if (result.success) {
        toast.success("Notification sent successfully!")
        // Refresh logs
        void logsQuery.refetch()
        void statsQuery.refetch()
      } else {
        toast.error(`Failed to send: ${result.error}`)
      }
    } catch (error) {
      toast.error("Error sending notification")
      console.error(error)
    }
  }

  const handleQuickTest = async (channel: "slack" | "telegram" | "email" | "desktop") => {
    try {
      const result = await quickNotifyMutation.mutateAsync({
        message: `Test notification from ${channel} - ${new Date().toLocaleTimeString()}`,
        title: "Test Notification",
        channels: [channel],
        priority: "low",
      })

      if (Array.isArray(result.results) ? result.results.some((r) => r.success) : result.result?.success) {
        toast.success(`${channel} test successful!`)
      } else {
        toast.error(`${channel} test failed`)
      }
    } catch (error) {
      toast.error("Test failed")
      console.error(error)
    }
  }

  const addAction = () => {
    setFormData((prev) => ({
      ...prev,
      actions: [...prev.actions, { id: `action-${Date.now()}`, text: "", value: "" }],
    }))
  }

  const removeAction = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index),
    }))
  }

  const updateAction = (index: number, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      actions: prev.actions.map((action, i) => (i === index ? { ...action, [field]: value } : action)),
    }))
  }

  const tabs = [
    { id: "send" as TabType, label: "📤 Send Notification", icon: "📤" },
    { id: "preferences" as TabType, label: "⚙️ Preferences", icon: "⚙️" },
    { id: "analytics" as TabType, label: "📊 Analytics", icon: "📊" },
    { id: "logs" as TabType, label: "📋 Logs", icon: "📋" },
  ]

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-background rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">🔔</div>
          <h1 className="text-2xl font-bold ">Notification Center</h1>
        </div>
        <p className="text-primary">
          Unified notification system for Slack, Telegram, Email, and Desktop notifications
        </p>
      </div>

      {/* Quick Test Buttons */}
      <div className="bg-background rounded-lg border border-border p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">⚡ Quick Tests</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => handleQuickTest("slack")}
            disabled={quickNotifyMutation.isLoading}
            className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            <span className="text-2xl">💬</span>
            <span className="font-medium">Test Slack</span>
          </button>
          <button
            onClick={() => handleQuickTest("telegram")}
            disabled={quickNotifyMutation.isLoading}
            className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            <span className="text-2xl">📱</span>
            <span className="font-medium">Test Telegram</span>
          </button>
          <button
            onClick={() => handleQuickTest("email")}
            disabled={quickNotifyMutation.isLoading}
            className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            <span className="text-2xl">📧</span>
            <span className="font-medium">Test Email</span>
          </button>
          <button
            onClick={() => handleQuickTest("desktop")}
            disabled={quickNotifyMutation.isLoading}
            className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            <span className="text-2xl">🖥️</span>
            <span className="font-medium">Test Desktop</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-background rounded-lg border border-border">
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-primary hover:border-gray-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Send Notification Tab */}
          {activeTab === "send" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Send Custom Notification</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Settings */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">Channel</label>
                    <select
                      value={formData.channel}
                      onChange={(e) => setFormData((prev) => ({ ...prev, channel: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="slack">💬 Slack</option>
                      <option value="telegram">📱 Telegram</option>
                      <option value="email">📧 Email</option>
                      <option value="desktop">🖥️ Desktop</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Notification title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">Message</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Notification message"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-primary mb-2">Priority</label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData((prev) => ({ ...prev, priority: e.target.value as any }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="low">📝 Low</option>
                        <option value="medium">ℹ️ Medium</option>
                        <option value="high">❗ High</option>
                        <option value="urgent">🚨 Urgent</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary mb-2">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value as any }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="system">System</option>
                        <option value="user">User</option>
                        <option value="business">Business</option>
                        <option value="personal">Personal</option>
                        <option value="marketing">Marketing</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Channel-Specific Settings */}
                <div className="space-y-4">
                  <h3 className="font-medium text-primary">Channel Settings</h3>

                  {formData.channel === "slack" && (
                    <div>
                      <label className="block text-sm font-medium text-primary mb-2">Slack Channel</label>
                      <input
                        type="text"
                        value={formData.slackChannelId || ""}
                        onChange={(e) => setFormData((prev) => ({ ...prev, slackChannelId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g. #general or #alert-monitors"
                      />
                    </div>
                  )}

                  {formData.channel === "telegram" && (
                    <div>
                      <label className="block text-sm font-medium text-primary mb-2">Telegram Chat ID</label>
                      <input
                        type="text"
                        value={formData.telegramChatId || ""}
                        onChange={(e) => setFormData((prev) => ({ ...prev, telegramChatId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g. -4761132014 or user ID"
                      />
                    </div>
                  )}

                  {formData.channel === "email" && (
                    <div>
                      <label className="block text-sm font-medium text-primary mb-2">Email To</label>
                      <input
                        type="email"
                        value={formData.emailTo || ""}
                        onChange={(e) => setFormData((prev) => ({ ...prev, emailTo: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="recipient@example.com"
                      />
                    </div>
                  )}

                  {/* Interactive Features */}
                  <div className="pt-4 border-t border-border">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.interactive}
                        onChange={(e) => setFormData((prev) => ({ ...prev, interactive: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-primary">Enable Interactive Actions</span>
                    </label>

                    {formData.interactive && (
                      <div className="mt-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-primary">Actions</span>
                          <button
                            type="button"
                            onClick={addAction}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            + Add Action
                          </button>
                        </div>

                        {formData.actions.map((action, index) => (
                          <div key={action.id} className="p-3 border border-border rounded-lg space-y-2">
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={action.text}
                                onChange={(e) => updateAction(index, "text", e.target.value)}
                                placeholder="Button text"
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <input
                                type="text"
                                value={action.value}
                                onChange={(e) => updateAction(index, "value", e.target.value)}
                                placeholder="Action value"
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <button
                                type="button"
                                onClick={() => removeAction(index)}
                                className="px-2 py-1 text-sm text-destructive hover:text-destructive"
                              >
                                ×
                              </button>
                            </div>
                            <div className="flex gap-2">
                              <select
                                value={action.style || "default"}
                                onChange={(e) => updateAction(index, "style", e.target.value)}
                                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="default">Default</option>
                                <option value="primary">Primary</option>
                                <option value="danger">Danger</option>
                              </select>
                              <input
                                type="url"
                                value={action.url || ""}
                                onChange={(e) => updateAction(index, "url", e.target.value)}
                                placeholder="Optional URL"
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-border">
                <button
                  onClick={handleSendNotification}
                  disabled={sendMutation.isLoading || !formData.title || !formData.message}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {sendMutation.isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <span>📤</span>
                      Send Notification
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === "preferences" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Notification Preferences</h2>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="text-yellow-600 text-xl">⚠️</span>
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">Coming Soon</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      User preference management UI is being developed. For now, preferences can be set via tRPC API.
                    </p>
                  </div>
                </div>
              </div>

              {userPrefsQuery.data?.preferences && (
                <div className="bg-foregroundx border border-border rounded-lg p-4">
                  <h3 className="font-medium text-primary mb-2">Current Preferences</h3>
                  <pre className="text-sm text-primary overflow-x-auto">
                    {JSON.stringify(userPrefsQuery.data.preferences, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === "analytics" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Analytics & Performance</h2>

              {statsQuery.data ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-600">{statsQuery.data.stats.totalSent}</div>
                    <div className="text-sm text-blue-600">Total Sent (24h)</div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">{statsQuery.data.stats.successful}</div>
                    <div className="text-sm text-green-600">Successful</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-2xl font-bold text-destructive">{statsQuery.data.stats.failed}</div>
                    <div className="text-sm text-destructive">Failed</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-600">
                      {statsQuery.data.stats.totalSent > 0
                        ? `${Math.round((statsQuery.data.stats.successful / statsQuery.data.stats.totalSent) * 100)}%`
                        : "N/A"}
                    </div>
                    <div className="text-sm text-purple-600">Success Rate</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">Loading analytics...</div>
              )}

              {statsQuery.data && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-background border border-border rounded-lg p-4">
                    <h3 className="font-medium text-primary mb-3">By Channel</h3>
                    <div className="space-y-2">
                      {Object.entries(statsQuery.data.stats.byChannel).map(([channel, count]) => (
                        <div key={channel} className="flex justify-between items-center">
                          <span className="text-sm text-primary capitalize">{channel}</span>
                          <span className="text-sm font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-background border border-border rounded-lg p-4">
                    <h3 className="font-medium text-primary mb-3">By Priority</h3>
                    <div className="space-y-2">
                      {Object.entries(statsQuery.data.stats.byPriority).map(([priority, count]) => (
                        <div key={priority} className="flex justify-between items-center">
                          <span className="text-sm text-primary capitalize">{priority}</span>
                          <span className="text-sm font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === "logs" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Recent Notification Logs</h2>

              {logsQuery.data ? (
                <div className="bg-background border border-border rounded-lg overflow-hidden">
                  <ReactTable
                    data={logsQuery.data.logs}
                    columns={[
                      {
                        Header: "Time",
                        accessor: "sentAt",
                        Cell: (info) => new Date(info.getValue()).toLocaleString(),
                      },
                      {
                        Header: "Channel",
                        accessor: "channel",
                        Cell: (info) => (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                            {info.getValue()}
                          </span>
                        ),
                      },
                      {
                        Header: "Priority",
                        accessor: "priority",
                        Cell: (info) => {
                          const priority = info.getValue()
                          const colors = {
                            low: "bg-foregroundx text-gray-800",
                            medium: "bg-blue-100 text-blue-800",
                            high: "bg-yellow-100 text-yellow-800",
                            urgent: "bg-red-100 text-destructive",
                          }
                          return (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                colors[priority as keyof typeof colors]
                              }`}
                            >
                              {priority}
                            </span>
                          )
                        },
                      },
                      {
                        Header: "Title",
                        accessor: "title",
                        Cell: (info) => <div className="max-w-xs truncate">{info.getValue()}</div>,
                      },
                      {
                        Header: "Message",
                        accessor: "message",
                        Cell: (info) => <div className="max-w-md truncate text-primary">{info.getValue()}</div>,
                      },
                      {
                        Header: "Status",
                        accessor: "success",
                        Cell: (info) => (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              info.getValue() ? "bg-green-100 text-green-800" : "bg-red-100 text-destructive"
                            }`}
                          >
                            {info.getValue() ? "✅ Success" : "❌ Failed"}
                          </span>
                        ),
                      },
                    ]}
                    emptyState={<div className="text-center py-8 text-gray-500">No notification logs found</div>}
                  />
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">Loading logs...</div>
              )}

              <div className="text-sm text-gray-500">
                Showing last 50 notifications. Logs are kept in memory and will reset on server restart.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
